import dayjs from "dayjs";

function escapeHtml(value) {
    return String(value || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
}

export function buildSharedItemEmailHtml({
    actionLabel,
    assetType,
    link,
    message,
    title,
}) {
    const safeTitle = escapeHtml(title);
    const safeType = escapeHtml(assetType);
    const safeActionLabel = escapeHtml(actionLabel);
    const safeLink = escapeHtml(link);
    const messageSection = message
        ? `<tr><td style="padding: 0 30px 20px 30px; font-family: Arial, Helvetica, sans-serif; font-size: 15px; line-height: 1.5; color: #555555;">${escapeHtml(
              message,
          ).replace(/\n/g, "<br>")}</td></tr>`
        : "";

    return `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1.0"></head>
<body style="margin: 0; padding: 0; background-color: #f4f4f4; font-family: Arial, Helvetica, sans-serif;">
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background-color: #f4f4f4;">
        <tr><td align="center" style="padding: 20px 0;">
            <table role="presentation" width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
                <tr><td style="background-color: rgb(0, 149, 61); padding: 20px 30px;"><h1 style="margin: 0; font-family: Arial, Helvetica, sans-serif; font-size: 22px; color: #ffffff;">Nodiant Online</h1></td></tr>
                <tr><td style="padding: 30px 30px 10px 30px; font-family: Arial, Helvetica, sans-serif; font-size: 15px; line-height: 1.5; color: #333333;">A ${safeType} has been shared with you:</td></tr>
                <tr><td style="padding: 0 30px 20px 30px; font-family: Arial, Helvetica, sans-serif; font-size: 18px; font-weight: bold; color: #333333;">${safeTitle}</td></tr>
                ${messageSection}
                <tr><td style="padding: 0 30px 30px 30px;"><a href="${safeLink}" style="display: inline-block; padding: 12px 24px; background-color: rgb(0, 149, 61); color: #ffffff; text-decoration: none; border-radius: 4px; font-family: Arial, Helvetica, sans-serif; font-size: 15px; font-weight: bold;">${safeActionLabel}</a></td></tr>
                <tr><td style="background-color: #ffffff; border-top: 1px solid #e0e0e0; padding: 15px 30px; text-align: center;"><img src="https://firebasestorage.googleapis.com/v0/b/nodiant-e20a8.appspot.com/o/FAW_CLEAR.png?alt=media&amp;token=a5b922df-5982-4fd9-af85-5609bb390d55" alt="FAW Logo" height="40" style="height: 40px;"></td></tr>
            </table>
        </td></tr>
    </table>
</body>
</html>`;
}

function describeAudience(userCount, groupCount) {
    const parts = [];

    if (userCount > 0) {
        parts.push(`${userCount} ${userCount === 1 ? "user" : "users"}`);
    }
    if (groupCount > 0) {
        parts.push(`${groupCount} ${groupCount === 1 ? "group" : "groups"}`);
    }

    return parts.join(" and ");
}

// Sharing used to report itself twice: the page announced who it was shared
// with and the dialog separately announced what was delivered. Both facts
// belong in one message, built here so every share dialog words it the same.
export function buildShareFeedbackMessage({
    assetLabel,
    embargoNotifications,
    groupCount,
    notificationDate,
    sendEmails,
    sendNotifications,
    userCount,
}) {
    const audience = describeAudience(userCount, groupCount);
    const parts = [
        audience
            ? `${assetLabel} shared with ${audience}`
            : `${assetLabel} access updated`,
    ];

    const isScheduled =
        embargoNotifications &&
        dayjs(notificationDate).isAfter(dayjs(), "day");
    const scheduledFor = dayjs(notificationDate).format("DD/MM/YYYY");

    if (sendNotifications) {
        parts.push(
            isScheduled
                ? `notifications scheduled for ${scheduledFor}`
                : "notifications sent",
        );
    }
    if (sendEmails) {
        parts.push(
            isScheduled
                ? `emails scheduled for ${scheduledFor}`
                : "emails sent",
        );
    }

    return parts.join(" · ");
}

// sendUserNotification rejects more than 500 recipients in one call.
const MAX_RECIPIENTS_PER_CALL = 500;

/**
 * Hands the send to sendUserNotification, which resolves each recipient's
 * address and notification preference server-side from their uid. The client
 * never sees an address.
 *
 * An embargoed send is queued with uid-shaped recipients and resolved at
 * delivery time, so someone who opts out between queueing and sending is no
 * longer emailed anyway — the old queue captured addresses up front and could
 * not honour that.
 */
export function sendSharedItemEmails({
    embargoNotifications,
    firebase,
    html,
    label,
    notificationDate,
    recipients,
    subject,
}) {
    // /users is keyed by auth uid, so a recipient's key IS their uid — the
    // same identity the notification fan-out already writes.
    const uids = recipients.map((user) => user.key).filter(Boolean);

    if (uids.length === 0) {
        return Promise.resolve();
    }

    const isEmbargoed =
        embargoNotifications &&
        dayjs(notificationDate).isAfter(dayjs(), "day");

    const sendUserNotification = firebase
        .functions()
        .httpsCallable("sendUserNotification");

    const payload = { subject, html };

    if (isEmbargoed) {
        payload.sendAfter = dayjs(notificationDate)
            .startOf("day")
            .hour(9)
            .toISOString();
    }

    if (label) {
        payload.label = label;
    }

    const batches = [];
    for (let i = 0; i < uids.length; i += MAX_RECIPIENTS_PER_CALL) {
        batches.push(uids.slice(i, i + MAX_RECIPIENTS_PER_CALL));
    }

    return Promise.all(
        batches.map((batch) =>
            sendUserNotification({ ...payload, uids: batch }),
        ),
    );
}
